import PropTypes from 'prop-types';
import { Helmet } from 'core/libs/helmet';

import { withRouter } from 'core/libs/router';

import { getRelLinkTag } from 'core/utils/dom';
import modelPropTypes, {
  rubricAttributes,
  ancestryAttributes,
} from 'core/utils/prop-types/model';
import { replaceCurrentYear } from 'core/utils/string-helper';

import ListPage from 'core/components/ListPage';
import H1 from 'core/components/H1';
import MarkdownWrapper from 'core/components/MarkdownWrapper';
import RubricTags from 'core/components/RubricTags';
import RubricSelect from 'core/components/RubricSelect';


/**
 * @author Aleksey Dibrivniy <a.dibrivniy@rambler-co.ru>
 */
function Rubric(props, { host, amp, features }) {
  const {
    children,
    limit,
    rawTopics,
    rawRubric,
    meta,
    pagination,
    skipRcm,
    titleComponent: TitleComponent,
    headerSpacing,
    showSubrubrics,
    typeOfSubrubrics,
    subrubricsColumnCount,
    compactPaging,
    location,
    isLoading,
    hideAmpLink,
  } = props;

  const {
    title,
    description,
    meta_title: metaTitle,
    meta_description: metaDescription,
  } = rawRubric?.data?.attributes || {};

  const titleMeta = replaceCurrentYear(metaTitle);
  const descriptionMeta = replaceCurrentYear(metaDescription);

  const interitemSpacing = 20;

  return (
    <ListPage
      title={titleMeta || `${title} — все статьи и новости`}
      description={descriptionMeta}
      rawData={rawTopics}
      meta={meta}
      limit={limit}
      skipRcm={skipRcm}
      pagination={pagination}
      compactPaging={compactPaging}
      isLoading={isLoading}
    >
      {({ content }) => {
        return (
          <>
            {!features.disableAmp && !hideAmpLink && (
              <Helmet>
                {getRelLinkTag(amp, host, location)}
              </Helmet>
            )}
            <TitleComponent rawRubric={rawRubric} title={title}>
              {title}
            </TitleComponent>
            {showSubrubrics && (
              <div style={{ marginTop: interitemSpacing }}>
                {typeOfSubrubrics === 'tags' ? (
                  <RubricTags rubrics={rawRubric}  columnCount={subrubricsColumnCount} />
                ) : (
                  <RubricSelect rubrics={rawRubric} />
                )}
              </div>
            )}
            {description && (
              <div style={{ marginTop: interitemSpacing }}>
                <MarkdownWrapper>{description}</MarkdownWrapper>
              </div>
            )}
            {headerSpacing > 0 && <div style={{ marginTop: headerSpacing }} />}
            {children({
              topics: content,
              rawRubric,
              title,
              titleMeta,
              descriptionMeta,
            })}
          </>
        );
      }}
    </ListPage>
  );
}

Rubric.contextTypes = {
  host: PropTypes.string,
  amp: PropTypes.bool,
  features: PropTypes.shape({
    disableAmp: PropTypes.bool,
  }),
};

Rubric.defaultProps = {
  children: () => null,
  titleComponent: H1,
  headerSpacing: 30,
  typeOfSubrubrics: 'tags',
};

Rubric.propTypes = {
  /**
   * Компонент кастомной паджинации
   */
  pagination: PropTypes.func,
  /**
   * Функция с данными для рендера
   */
  children: PropTypes.func.isRequired,

  /**
   * Сырые НЕденормализованые данные от апишки
   */
  rawTopics: PropTypes.shape({
    data: PropTypes.array,
    included: PropTypes.array,
    meta: PropTypes.object,
  }),

  /**
   * Данные о текущей рубрике от апишки
   */
  rawRubric: PropTypes.shape({
    data: modelPropTypes(rubricAttributes),
    meta: PropTypes.shape({
      ancestry: ancestryAttributes,
    }),
  }),

  /**
   * Мета-данные для пагинации ListPage.
   *
   * ⚠️ **Заметки:**<br/>
   * Данные из `rawTopics.meta` будут переопределены.
   */
  meta: PropTypes.object,

  /**
   * Максимальное количество топиков на одну страницу.
   *
   * Свойство используется при запросе топиков и компонентом ListPage.
   */
  limit: PropTypes.number,

  /**
   * Отключить отправку событий RCM.
   */
  skipRcm: PropTypes.bool,

  /**
   * Для скрытия амп-ссылки в мета-тегах `link[rel='amphtml']`.
   */
  hideAmpLink: PropTypes.bool,

  /**
   * Компонент для отображения заголовка
   */
  titleComponent: PropTypes.func.isRequired,

  /**
   * Показать подрубрики
   */
  showSubrubrics: PropTypes.bool,

  /**
   * Тип компонента для подрубрик
   */
  typeOfSubrubrics: PropTypes.oneOf(['tags', 'select']),

  /**
   * Отступ от заголовка
   */
  headerSpacing: PropTypes.number,

  /**
   * columnCount для подрубрик
   */
  subrubricsColumnCount: PropTypes.number,

  /**
   * Компактный режим отображаения пагинации
   */
  compactPaging: PropTypes.bool,

  /** Признак загрузки данных для передачи в ListPage */
  isLoading: PropTypes.bool,

  /** @ignore */
  location: PropTypes.object,
};


const RubricWithHOCs = withRouter(Rubric);

RubricWithHOCs.displayName = 'Rubric';

export { Rubric as StorybookComponent };
export default RubricWithHOCs;
